import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CashReportDetailFilters, getCashReportDetailThunk } from '@spike/payments-action';
import { CashReportDetailCategory, CashReportDetailPage, CashReportDetailRow } from '@spike/payments-model';
import { Moment } from 'moment-timezone';
import Button from 'components/UI/V3/Button/Button';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import Spinner from 'components/UI/V3/Spinner';
import InvoiceDetail from 'components/Payments/Transactions/Invoices/InvoiceDetail';
import PaymentMethod from 'components/Payments/Transactions/PaymentMethod';
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { RootState } from 'store';
import { formatSalesReportItemValue, salesReportItemvalueType } from 'utils/PaymentsUtils';
import CashReportDetailExportCSV from './CashReportDetailExportCSV';
import { categoryLabel } from './cashReportDetailLabels';
import styles from './CashReportDetail.module.scss';

interface CashReportDetailProps {
    category: CashReportDetailCategory;
    from: Moment | null;
    to: Moment | null;
    marketplaceIds: Array<number>;
    onBack: () => void;
}

interface SelectedInvoice {
    invoiceId: number;
    marketplaceId: number;
}

const formatPeriod = (from: Moment | null, to: Moment | null) => {
    if (!from || !to) {
        return 'All Time';
    }

    return from.isSame(to, 'day')
        ? from.format('MMMM DD, YYYY')
        : `${from.format('MMMM DD, YYYY')} - ${to.format('MMMM DD, YYYY')}`;
};

const CashReportDetail: React.FC<CashReportDetailProps> = ({ category, from, to, marketplaceIds, onBack }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const { organizationMarketplaces, getOrganizationMarketplace } = useOrganizationMarketplaces();

    const [size, setSize] = useState(10);
    const [data, setData] = useState<CashReportDetailPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState<SelectedInvoice | null>(null);
    const requestId = useRef(0);

    // The page goes back to 1 whenever the report filters change, without fetching twice.
    const filtersKey = `${category}|${from?.format('YYYY-MM-DD')}|${to?.format('YYYY-MM-DD')}|${marketplaceIds.join(
        ','
    )}`;
    const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
    const page = pageState.key === filtersKey ? pageState.page : 1;

    const filters: CashReportDetailFilters = useMemo(
        () => ({ category, from, to, marketplaceIds }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filtersKey]
    );

    useEffect(() => {
        const currentRequest = ++requestId.current;
        setLoading(true);

        dispatch(getCashReportDetailThunk(apiClientWrapper, { ...filters, page, perPage: size }))
            .then(result => {
                if (currentRequest === requestId.current) setData(result);
            })
            .catch(() => {
                // the thunk already shows the error alert
                if (currentRequest === requestId.current) setData(null);
            })
            .finally(() => {
                if (currentRequest === requestId.current) setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page, size]);

    const changePageHandler = (nextPage: number) => setPageState({ key: filtersKey, page: nextPage });

    const changeSizeHandler = (nextSize: number) => {
        setSize(nextSize);
        changePageHandler(1);
    };

    const openInvoiceHandler = (row: CashReportDetailRow) => {
        const marketplaceId = row.marketplaceId ?? marketplace.id;

        if (marketplaceId !== undefined) {
            setSelectedInvoice({ invoiceId: row.invoiceId, marketplaceId });
        }
    };

    // Same rule as Transactions: the location is only shown for multi-location organizations.
    const renderLocation = (row: CashReportDetailRow) => {
        const marketplaceId = row.marketplaceId ?? marketplace.id;
        const locationName = marketplaceId !== undefined ? getOrganizationMarketplace(marketplaceId)?.name : undefined;

        if (organizationMarketplaces.length <= 1 || !locationName) return null;

        return (
            <div className={styles.location}>
                <FontAwesomeIcon
                    icon={faLocationDot}
                    className={styles.locationIcon}
                />
                {locationName}
            </div>
        );
    };

    const isRefund = category === 'refund';
    const rows = data?.rows ?? [];

    const renderBody = () => {
        if (loading && !data) {
            return (
                <div className={styles.message}>
                    <Spinner />
                </div>
            );
        }

        if (rows.length === 0) {
            return (
                <div className={styles.message}>
                    <h3 className={styles.messageTitle}>No transactions found</h3>
                    <p className={styles.messageText}>
                        There are no {categoryLabel(category).toLowerCase()} in the selected period.
                    </p>
                </div>
            );
        }

        return (
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.headerCell}>Date</th>
                            <th className={styles.headerCell}>Client</th>
                            {!isRefund && <th className={styles.headerCell}>Item</th>}
                            <th className={styles.headerCell}>Invoice</th>
                            <th className={styles.headerCell}>Paid Type</th>
                            <th className={`${styles.headerCell} ${styles.number}`}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={`${row.paymentId ?? row.refundId}-${index}`}>
                                <td className={`${styles.cell} ${styles.nowrap}`}>
                                    {row.occurredAt.format('MMM D, YYYY')}
                                    <div className={styles.muted}>{row.occurredAt.format('h:mm A')}</div>
                                    {renderLocation(row)}
                                </td>
                                <td className={styles.cell}>{row.customerName}</td>
                                {!isRefund && (
                                    <td className={styles.cell}>
                                        {row.itemName}
                                        {row.quantity != null && row.quantity > 1 && (
                                            <span className={styles.muted}> × {row.quantity}</span>
                                        )}
                                    </td>
                                )}
                                <td className={`${styles.cell} ${styles.nowrap}`}>
                                    <button
                                        type="button"
                                        className={styles.invoiceLink}
                                        onClick={() => openInvoiceHandler(row)}
                                    >
                                        {row.invoiceNumber}
                                    </button>
                                </td>
                                <td className={`${styles.cell} ${styles.nowrap}`}>
                                    <PaymentMethod
                                        paymentMethodId={row.paymentMethodId}
                                        paymentMethodName={
                                            masterData.payments.paymentMethods.find(
                                                method => method.id === row.paymentMethodId
                                            )?.name ?? ''
                                        }
                                        cardDigits={row.cardDigits}
                                    />
                                </td>
                                <td className={`${styles.cell} ${styles.number}`}>
                                    {formatSalesReportItemValue(row.amount, salesReportItemvalueType.Money)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className={styles.root}>
            <Button
                label="Back to Sales Reports"
                leftIcon={faArrowLeft}
                variant="outlined"
                className={styles.back}
                onClick={onBack}
            />
            <div className={styles.card}>
                <div className={styles.head}>
                    <div>
                        <h2 className={styles.title}>{categoryLabel(category)}</h2>
                        <p className={styles.subtitle}>{formatPeriod(from, to)}</p>
                    </div>
                    <div className={styles.summary}>
                        {data && (
                            <span className={styles.total}>
                                Period total:{' '}
                                <span className={styles.totalAmount}>
                                    {formatSalesReportItemValue(data.totalAmount, salesReportItemvalueType.Money)}
                                </span>{' '}
                                ({data.totalCount} {data.totalCount === 1 ? 'entry' : 'entries'})
                            </span>
                        )}
                        <CashReportDetailExportCSV filters={filters} />
                    </div>
                </div>

                {renderBody()}

                {data && rows.length > 0 && (
                    <div className={styles.paginationWrap}>
                        <Pagination
                            page={data.page}
                            totalPages={data.totalPages}
                            size={data.perPage}
                            isLoading={loading}
                            onPageChange={changePageHandler}
                            onSizeChange={changeSizeHandler}
                        />
                    </div>
                )}
            </div>

            {selectedInvoice && (
                <InvoiceDetail
                    invoiceId={selectedInvoice.invoiceId}
                    marketplaceId={selectedInvoice.marketplaceId}
                    marketplaceName={getOrganizationMarketplace(selectedInvoice.marketplaceId)?.name ?? ''}
                    isCheckoutDisabled
                    onClose={() => setSelectedInvoice(null)}
                />
            )}
        </div>
    );
};

export default CashReportDetail;
